import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Briefcase, Building2, Check, Loader2, Mail, MapPin, Pencil, Phone, Plus,
  Save, Search, Trash2, Upload, UserPlus, UserRound, Users, X,
} from "lucide-react";
import { toast } from "sonner";

import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { initials } from "@/lib/utils-ros";

const TEAM_DEPARTMENTS = ["All", "Founders", "Marketing", "Dev Ops", "Interns"];
const INPUT = "w-full h-10 px-3 rounded-xl border border-[#C9DCE6] bg-white text-[13px] text-[#243B53] outline-none focus:border-[#07539A]";

export default function PeopleDirectory({ type }) {
  const isPartners = type === "partners";
  const { role } = useAuth();
  const isAdmin = role === "super_admin" || role === "admin";
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [department, setDepartment] = useState("All");
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [pendingAssignments, setPendingAssignments] = useState([]);
  const [assignmentBusy, setAssignmentBusy] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(isPartners ? "/directory/partners" : "/directory/team");
      setPeople(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not load the directory");
      setPeople([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [type]);

  const loadPendingAssignments = useCallback(async () => {
    if (!isPartners) return;
    try {
      const { data } = await api.get("/partner-assignments/pending");
      setPendingAssignments(Array.isArray(data) ? data : []);
    } catch {
      setPendingAssignments([]);
    }
  }, [isPartners]);

  useEffect(() => { loadPendingAssignments(); }, [loadPendingAssignments]);

  async function decideAssignment(id, decision) {
    setAssignmentBusy(id);
    try {
      await api.post(`/partner-assignments/${id}/decision`, { decision });
      toast.success(decision === "accepted" ? "Customer assignment accepted" : "Assignment rejected · admin follow-up created");
      await loadPendingAssignments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update assignment");
    } finally { setAssignmentBusy(null); }
  }

  async function deleteContact(person) {
    if (!window.confirm(`Remove ${person.name}'s contact card? This can't be undone.`)) return;
    try {
      await api.delete(isPartners ? `/partner-contacts/${person.id}` : `/team-contacts/${person.id}`);
      toast.success("Contact card removed");
      load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not remove this contact");
    }
  }

  const departments = useMemo(() => {
    if (isPartners) return [];
    const extras = people.map(person => person.department).filter(Boolean).filter(value => !TEAM_DEPARTMENTS.includes(value));
    return [...TEAM_DEPARTMENTS, ...Array.from(new Set(extras)).sort()];
  }, [isPartners, people]);

  const filtered = useMemo(() => people.filter(person => {
    if (!isPartners && department !== "All" && person.department !== department) return false;
    const haystack = [person.name, person.designation, person.department, person.location, person.firm, person.region, person.email, person.phone]
      .filter(Boolean).join(" ").toLowerCase();
    return !query.trim() || haystack.includes(query.trim().toLowerCase());
  }), [department, isPartners, people, query]);

  const title = isPartners ? "Partners" : "Ergoniq Team";
  const subtitle = isPartners
    ? "People across our partner network — with their parent firm and direct contact details."
    : "Meet the people building and operating Ergoniq across every function.";

  return (
    <div className="min-h-full bg-[#F5FAFC] pb-12" data-testid={`${type}-directory-page`}>
      <section className="bg-[#07539A] px-5 md:px-9 pt-7 pb-16 shadow-[inset_0_-1px_0_rgba(255,255,255,0.12)]">
        <div className="flex items-start justify-between gap-5 flex-wrap">
          <div>
            <h1 className="font-display text-3xl md:text-4xl tracking-tight !text-white">{title}</h1>
            <p className="text-[13px] text-white/75 mt-1 max-w-2xl">{subtitle}</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-left md:text-right">
              <div className="text-[10px] uppercase tracking-widest text-white/60">People</div>
              <div className="font-display text-2xl tabular-nums !text-white">{people.length}</div>
            </div>
            {isAdmin && (
              <button onClick={() => setAdding(true)} data-testid={`${type}-directory-add`}
                      className="h-10 px-4 rounded-xl bg-[#E58D22] hover:bg-[#C97715] !text-white text-[12px] font-semibold flex items-center gap-2 shrink-0 shadow-[0_7px_18px_rgba(229,141,34,0.28)]">
                <Plus size={14} /> Add person
              </button>
            )}
          </div>
        </div>
      </section>

      <div className="px-4 md:px-8 -mt-7 relative z-10 space-y-5">
        {isPartners && pendingAssignments.length > 0 && (
          <section className="rounded-[22px] border border-[#BBD8E7] bg-[#F3FAFD] shadow-[0_12px_30px_rgba(7,83,154,0.12)] p-5" data-testid="pending-partner-assignments">
            <div className="flex items-center gap-2 mb-3">
              <Users size={16} className="text-[#07539A]" />
              <h2 className="font-display text-lg text-[#12324A]">Pending customer assignments</h2>
              <span className="ml-auto text-[10px] font-mono text-[#07539A]">{pendingAssignments.length}</span>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {pendingAssignments.map(item => (
                <article key={item.id} className="rounded-2xl border border-[#C9DCE6] bg-white p-4" data-testid={`pending-assignment-${item.id}`}>
                  <div className="text-[13px] font-semibold text-[#12324A]">{item.company?.name || "Customer"}</div>
                  <div className="text-[10px] text-[#78909C] mt-1">
                    {item.direction === "incoming" ? `Proposed by ${item.from_partner?.firm || item.from_partner?.name || "current partner"}` : `Awaiting ${item.partner_name}`}
                  </div>
                  {item.notes && <p className="text-[11px] text-[#526D7A] mt-2">{item.notes}</p>}
                  {item.direction === "incoming" && role === "partner" && (
                    <div className="flex gap-2 mt-3">
                      <button type="button" onClick={() => decideAssignment(item.id, "accepted")} disabled={assignmentBusy === item.id}
                        className="h-9 px-3 rounded-xl bg-[#159A68] text-white text-[11px] font-medium flex items-center gap-1.5 disabled:opacity-40" data-testid={`accept-assignment-${item.id}`}>
                        <Check size={12} /> Accept
                      </button>
                      <button type="button" onClick={() => decideAssignment(item.id, "rejected")} disabled={assignmentBusy === item.id}
                        className="h-9 px-3 rounded-xl border border-[#E49B96] text-[#B42318] text-[11px] font-medium disabled:opacity-40" data-testid={`reject-assignment-${item.id}`}>
                        Reject
                      </button>
                    </div>
                  )}
                </article>
              ))}
            </div>
          </section>
        )}
        <div className="w-full flex items-center gap-3 px-5 h-14 rounded-full bg-white border border-[#BFD5E0] shadow-[0_12px_30px_rgba(7,83,154,0.17)] focus-within:border-[#00A2D4]">
          <Search size={17} className="text-[#07539A] shrink-0" />
          <input value={query} onChange={event => setQuery(event.target.value)} placeholder={isPartners ? "Search by person, firm, region, or contact…" : "Search by person, designation, team, or location…"} className="flex-1 bg-transparent outline-none text-[14px] text-[#243B53] placeholder:text-[#9AAEB8]" data-testid={`${type}-directory-search`} />
          <span className="text-[10px] font-mono text-[#78909C]">{filtered.length}</span>
        </div>

        {!isPartners && (
          <div className="flex items-center gap-2 overflow-x-auto pb-1" data-testid="team-department-filter">
            {departments.map(item => {
              const count = item === "All" ? people.length : people.filter(person => person.department === item).length;
              return (
                <button key={item} onClick={() => setDepartment(item)} className={`h-9 px-3.5 rounded-full border text-[11px] font-semibold flex items-center gap-2 shrink-0 ${department === item ? "border-[#07539A] bg-[#07539A] !text-white shadow-[0_5px_14px_rgba(7,83,154,0.18)]" : "border-[#C9DCE6] bg-white text-[#607D8B] hover:bg-[#EDF7FB]"}`}>
                  {item} <span className={`font-mono text-[9px] ${department === item ? "!text-white/75" : "text-[#9AAEB8]"}`}>{count}</span>
                </button>
              );
            })}
          </div>
        )}

        {loading ? (
          <div className="p-12 rounded-[22px] border border-[#C9DCE6] bg-white text-[#78909C] flex items-center justify-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading people…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 rounded-[22px] border border-dashed border-[#B8D3DF] bg-white text-center text-[13px] text-[#78909C]">
            <span className="w-12 h-12 rounded-2xl bg-[#E7F5FB] text-[#07539A] flex items-center justify-center mx-auto mb-3"><Users size={20} /></span>
            No people match this view.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map(person => <PersonCard key={person.id} person={person} isPartners={isPartners} isAdmin={isAdmin} onEdit={() => setEditing(person)} onDelete={() => deleteContact(person)} />)}
          </div>
        )}
      </div>

      {editing && <DirectoryEditModal person={editing} isPartners={isPartners} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {adding && <AddPersonModal isPartners={isPartners} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}
    </div>
  );
}

function PersonCard({ person, isPartners, isAdmin, onEdit, onDelete }) {
  const designation = isPartners ? (person.designation || "Partner representative") : person.designation;
  const location = isPartners ? person.region : person.location;
  // Only a contact-only card can be deleted here — a real account (source
  // "account"/"firm") is deleted from User Administration or Partner Management.
  const isDeletableContact = person.source === "contact";
  return (
    <article className="relative rounded-[22px] border border-[#C9DCE6] bg-white overflow-hidden shadow-[0_9px_24px_rgba(7,83,154,0.07)]" data-testid={`directory-person-${person.id}`}>
      <div className="h-16 bg-[#E7F5FB] border-b border-[#D5E7EF]" />
      <div className="px-5 pb-5 -mt-9">
        <div className="flex items-start gap-3">
          <div className="w-[74px] h-[74px] rounded-2xl overflow-hidden bg-[#07539A] border-4 border-white shadow-[0_7px_18px_rgba(7,83,154,0.18)] flex items-center justify-center !text-white text-[18px] font-display shrink-0">
            {person.avatar_url ? <img src={person.avatar_url} alt={person.name} className="w-full h-full object-cover" /> : initials(person.name || "")}
          </div>
          <div className="flex-1 min-w-0 pt-10">
            <div className="text-[15px] font-semibold text-[#12324A] truncate">{person.name}</div>
            <div className="text-[11px] text-[#607D8B] truncate mt-0.5">{designation || "Designation not added"}</div>
          </div>
          {isAdmin && (
            <div className="flex items-center gap-1.5 mt-10">
              <button onClick={onEdit} title="Edit contact" className="w-8 h-8 rounded-xl border border-[#C9DCE6] bg-white text-[#07539A] flex items-center justify-center hover:bg-[#E7F5FB]" data-testid={`directory-edit-${person.id}`}><Pencil size={13} /></button>
              {isDeletableContact && <button onClick={onDelete} title="Delete contact card" className="w-8 h-8 rounded-xl border border-[#E49B96] bg-white text-[#B42318] flex items-center justify-center hover:bg-[#FFF1F0]" data-testid={`directory-delete-${person.id}`}><Trash2 size={13} /></button>}
            </div>
          )}
        </div>

        <div className="mt-4 space-y-2.5 text-[11px] text-[#607D8B]">
          {isPartners ? (
            <InfoRow icon={Building2} value={person.firm || "Parent firm not added"} strong />
          ) : (
            <InfoRow icon={Briefcase} value={person.department || "Team not added"} strong />
          )}
          <InfoRow icon={MapPin} value={location || "Location not added"} />
          <InfoRow icon={Mail} value={person.email || "Email not added"} href={person.email ? `mailto:${person.email}` : null} />
          <InfoRow icon={Phone} value={person.phone || "Phone not added"} href={person.phone ? `tel:${person.phone}` : null} />
        </div>
      </div>
    </article>
  );
}

function InfoRow({ icon: Icon, value, href, strong }) {
  const content = <><span className="w-7 h-7 rounded-lg bg-[#EDF7FB] text-[#07539A] flex items-center justify-center shrink-0"><Icon size={12} /></span><span className={`truncate ${strong ? "font-semibold text-[#435F70]" : ""}`}>{value}</span></>;
  return href ? <a href={href} className="flex items-center gap-2.5 hover:text-[#07539A]">{content}</a> : <div className="flex items-center gap-2.5">{content}</div>;
}

function DirectoryEditModal({ person, isPartners, onClose, onSaved }) {
  const isFirmCard = isPartners && person.source === "firm";
  const [form, setForm] = useState({
    name: person.name || "", email: person.email || "", phone: person.phone || "", avatar_url: person.avatar_url || "",
    firm: person.firm || "", region: person.region || "",
    designation: person.designation || "", department: person.department || "", location: person.location || "",
  });
  const [saving, setSaving] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);

  async function choosePhoto(event) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file");
    if (file.size > 8 * 1024 * 1024) return toast.error("Image is too large (maximum 8 MB)");
    setPhotoBusy(true);
    try {
      const avatar_url = await resizeImageToDataUrl(file, 360);
      setForm(current => ({ ...current, avatar_url }));
    }
    catch { toast.error("Could not read that image"); }
    finally { setPhotoBusy(false); }
  }

  async function save(event) {
    event.preventDefault();
    if (!form.name.trim()) return toast.error("Name is required");
    if (isFirmCard && !form.firm.trim()) return toast.error("Parent firm is required");
    setSaving(true);
    try {
      let url, payload;
      if (isFirmCard) {
        // This card IS the firm's own record (its primary contact) — editing it
        // edits the firm, same as Partner Management does.
        url = `/partners/${person.id}`;
        payload = { name: form.name.trim(), firm: form.firm.trim(), region: form.region.trim(), email: form.email.trim() || null, phone: form.phone.trim() || null, avatar_url: form.avatar_url };
      } else if (isPartners) {
        // An additional directory-only contact under a firm — no login involved.
        url = `/partner-contacts/${person.id}`;
        payload = { name: form.name.trim(), email: form.email.trim() || null, phone: form.phone.trim() || null, avatar_url: form.avatar_url, designation: form.designation.trim() || null };
      } else if (person.source === "account") {
        // A real login — editing here edits the account itself (User Administration
        // shows the same record).
        url = `/admin/users/${person.id}`;
        payload = { name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(), avatar_url: form.avatar_url, designation: form.designation.trim(), department: form.department.trim(), location: form.location.trim() };
      } else {
        // A directory-only team contact — no login involved.
        url = `/team-contacts/${person.id}`;
        payload = { name: form.name.trim(), email: form.email.trim() || null, phone: form.phone.trim() || null, avatar_url: form.avatar_url, designation: form.designation.trim() || null, department: form.department.trim() || null, location: form.location.trim() || null };
      }
      await api.patch(url, payload);
      toast.success("Directory card updated");
      onSaved();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not save this contact");
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-[#062E55]/55 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={save} onClick={event => event.stopPropagation()} className="w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-[24px] border border-[#BFD6E1] bg-[#EFF8FC] shadow-[0_28px_70px_rgba(7,42,73,0.28)]" data-testid="directory-edit-modal">
        <div className="px-5 py-4 bg-[#07539A] flex items-center gap-3 !text-white">
          <span className="w-8 h-8 rounded-xl bg-white/15 flex items-center justify-center"><UserRound size={15} /></span>
          <div className="font-display text-[17px] flex-1 !text-white">Edit contact card</div>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-full border border-white/25 flex items-center justify-center hover:bg-white/15"><X size={14} /></button>
        </div>

        <div className="p-5 overflow-y-auto max-h-[calc(90vh-132px)] space-y-5">
          <div className="flex items-center gap-4 rounded-2xl border border-[#D2E5ED] bg-white p-4">
            <div className="w-20 h-20 rounded-2xl overflow-hidden bg-[#07539A] !text-white flex items-center justify-center text-xl font-display shrink-0">{form.avatar_url ? <img src={form.avatar_url} alt="Preview" className="w-full h-full object-cover" /> : initials(form.name)}</div>
            <div><div className="text-[13px] font-semibold text-[#243B53]">Profile photo</div><div className="text-[10px] text-[#78909C] mt-0.5">Upload a JPG, PNG, or WebP image.</div><label className="mt-2 h-9 px-3 rounded-xl bg-[#07539A] !text-white text-[11px] font-semibold inline-flex items-center gap-2 cursor-pointer"><Upload size={12} />{photoBusy ? "Processing…" : "Upload photo"}<input type="file" accept="image/*" onChange={choosePhoto} className="hidden" /></label></div>
          </div>

          {isPartners && !isFirmCard && (
            <div className="text-[11px] text-[#78909C] bg-white border border-[#D2E5ED] rounded-xl px-3 py-2.5">
              <span className="font-semibold text-[#435F70]">{person.firm || "No firm"}</span>{person.region ? ` · ${person.region}` : ""} — firm and region are set on the partner firm record, not per person.
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            <EditField label="Name" value={form.name} onChange={value => setForm({ ...form, name: value })} />
            {isFirmCard && <EditField label="Parent firm" value={form.firm} onChange={value => setForm({ ...form, firm: value })} />}
            {isFirmCard && <EditField label="Region / location" value={form.region} onChange={value => setForm({ ...form, region: value })} />}
            {isPartners && !isFirmCard && <EditField label="Designation" value={form.designation} onChange={value => setForm({ ...form, designation: value })} />}
            {!isPartners && <EditField label="Designation" value={form.designation} onChange={value => setForm({ ...form, designation: value })} />}
            {!isPartners && <EditField label="Team" value={form.department} onChange={value => setForm({ ...form, department: value })} list="team-departments" />}
            {!isPartners && <EditField label="Location" value={form.location} onChange={value => setForm({ ...form, location: value })} />}
            <EditField label="Email" type="email" value={form.email} onChange={value => setForm({ ...form, email: value })} />
            <EditField label="Phone" value={form.phone} onChange={value => setForm({ ...form, phone: value })} />
          </div>
          <datalist id="team-departments">{TEAM_DEPARTMENTS.filter(item => item !== "All").map(item => <option key={item} value={item} />)}</datalist>
        </div>

        <div className="px-5 py-4 border-t border-[#D2E5ED] bg-white flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="h-10 px-4 rounded-xl border border-[#C9DCE6] text-[#607D8B] text-[12px]">Cancel</button>
          <button type="submit" disabled={saving || photoBusy} className="h-10 px-5 rounded-xl bg-[#07539A] hover:bg-[#063F73] !text-white text-[12px] font-semibold flex items-center gap-2 disabled:opacity-50"><Save size={13} />{saving ? "Saving…" : "Save changes"}</button>
        </div>
      </form>
    </div>
  );
}

function AddPersonModal({ isPartners, onClose, onSaved }) {
  const [firms, setFirms] = useState([]);
  const [firmsLoading, setFirmsLoading] = useState(isPartners);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", partner_id: "",
    designation: "", department: "", location: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isPartners) return;
    let cancelled = false;
    api.get("/partner-firms").then(({ data }) => { if (!cancelled) setFirms(Array.isArray(data) ? data : []); })
      .catch(() => toast.error("Could not load partner firms"))
      .finally(() => { if (!cancelled) setFirmsLoading(false); });
    return () => { cancelled = true; };
  }, [isPartners]);

  async function submit(event) {
    event.preventDefault();
    if (!form.name.trim()) return toast.error("Name is required");
    if (isPartners && !form.partner_id) return toast.error("Select which firm this person is from");
    setSaving(true);
    try {
      // Directory-only contact — no login, no password, just a card that shows up
      // right away. Real accounts are created from User Administration / Partner
      // Management only, and show up here automatically once they exist.
      if (isPartners) {
        await api.post("/partner-contacts", {
          partner_id: form.partner_id, name: form.name.trim(),
          email: form.email.trim() || null, phone: form.phone.trim() || null,
        });
      } else {
        await api.post("/team-contacts", {
          name: form.name.trim(), email: form.email.trim() || null, phone: form.phone.trim() || null,
          designation: form.designation.trim() || null, department: form.department.trim() || null,
          location: form.location.trim() || null,
        });
      }
      toast.success("Person added");
      onSaved();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not add this person");
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-[#062E55]/55 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={event => event.stopPropagation()} className="w-full max-w-lg rounded-[24px] border border-[#BFD6E1] bg-[#EFF8FC] shadow-[0_28px_70px_rgba(7,42,73,0.28)]" data-testid="directory-add-modal">
        <div className="px-5 py-4 bg-[#07539A] flex items-center gap-3 !text-white">
          <span className="w-8 h-8 rounded-xl bg-white/15 flex items-center justify-center"><UserPlus size={15} /></span>
          <div className="font-display text-[17px] flex-1 !text-white">{isPartners ? "Add a person from a firm" : "Add a person"}</div>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-full border border-white/25 flex items-center justify-center hover:bg-white/15"><X size={14} /></button>
        </div>

        <div className="p-5 space-y-3">
          {isPartners && (
            <label>
              <span className="block text-[10px] uppercase tracking-widest font-semibold text-[#07539A] mb-1.5">Firm</span>
              <select value={form.partner_id} onChange={e => setForm({ ...form, partner_id: e.target.value })} className={INPUT} disabled={firmsLoading}>
                <option value="">{firmsLoading ? "Loading firms…" : "Select a partner firm…"}</option>
                {firms.map(f => <option key={f.id} value={f.id}>{f.firm || f.name}</option>)}
              </select>
            </label>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            <EditField label="Name" value={form.name} onChange={value => setForm({ ...form, name: value })} />
            <EditField label="Email" type="email" value={form.email} onChange={value => setForm({ ...form, email: value })} />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <EditField label="Phone" value={form.phone} onChange={value => setForm({ ...form, phone: value })} />
            {!isPartners && <EditField label="Designation" value={form.designation} onChange={value => setForm({ ...form, designation: value })} />}
          </div>
          {!isPartners && (
            <div className="grid sm:grid-cols-2 gap-3">
              <EditField label="Team" value={form.department} onChange={value => setForm({ ...form, department: value })} list="team-departments" />
              <EditField label="Location" value={form.location} onChange={value => setForm({ ...form, location: value })} />
              <datalist id="team-departments">{TEAM_DEPARTMENTS.filter(item => item !== "All").map(item => <option key={item} value={item} />)}</datalist>
            </div>
          )}
          <p className="text-[11px] text-[#78909C]">
            This adds a directory card only — no login or password. To give this person product access, create their account from {isPartners ? "Partner Management" : "User Administration"} instead.
          </p>
        </div>

        <div className="px-5 py-4 border-t border-[#D2E5ED] bg-white flex items-center justify-end gap-2 rounded-b-[24px]">
          <button type="button" onClick={onClose} className="h-10 px-4 rounded-xl border border-[#C9DCE6] text-[#607D8B] text-[12px]">Cancel</button>
          <button type="submit" disabled={saving} className="h-10 px-5 rounded-xl bg-[#07539A] hover:bg-[#063F73] !text-white text-[12px] font-semibold flex items-center gap-2 disabled:opacity-50"><UserPlus size={13} />{saving ? "Adding…" : "Add person"}</button>
        </div>
      </form>
    </div>
  );
}

function EditField({ label, value, onChange, type = "text", list }) {
  return <label><span className="block text-[10px] uppercase tracking-widest font-semibold text-[#07539A] mb-1.5">{label}</span><input type={type} list={list} value={value} onChange={event => onChange(event.target.value)} className={INPUT} /></label>;
}

function resizeImageToDataUrl(file, maxSize) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const image = new Image();
      image.onerror = reject;
      image.onload = () => {
        const scale = Math.min(1, maxSize / Math.max(image.width, image.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(image.width * scale));
        canvas.height = Math.max(1, Math.round(image.height * scale));
        canvas.getContext("2d").drawImage(image, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.86));
      };
      image.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}
